export const BOOPLY_ENVIRONMENTS = ["local", "development", "staging", "production"] as const;

export type BooplyEnvironment = (typeof BOOPLY_ENVIRONMENTS)[number];

export interface ProductionSafetyIssue {
  readonly code: string;
  readonly message: string;
}

export interface ProductionSafetyOptions {
  readonly environment?: string | null;
  readonly apiBaseUrl?: string | null;
  readonly relayUrl?: string | null;
  readonly fakeBillingEnabled?: boolean;
  readonly adminMockModeEnabled?: boolean;
  readonly mockDataEnabled?: boolean;
  readonly mockModerationProviderEnabled?: boolean;
  readonly mockModerationSafePlaceholder?: boolean;
}

export function normalizeBooplyEnvironment(
  value: string | null | undefined,
  fallback: BooplyEnvironment = "local"
): BooplyEnvironment {
  const normalized = value?.trim().toLowerCase();

  switch (normalized) {
    case "local":
      return "local";
    case "dev":
    case "development":
    case "test":
      return "development";
    case "stage":
    case "staging":
      return "staging";
    case "prod":
    case "production":
      return "production";
    default:
      return fallback;
  }
}

export function fakeBillingEnabledForEnvironment(value: string | null | undefined): boolean {
  return normalizeBooplyEnvironment(value, "production") !== "production";
}

export function developmentControlsVisibleForEnvironment(value: string | null | undefined, productionFlag = false): boolean {
  return !productionFlag && normalizeBooplyEnvironment(value, "development") !== "production";
}

export function adminMockModeEnabledForEnvironment(
  environmentValue: string | null | undefined,
  configuredValue: string | boolean | null | undefined,
  productionFlag = false
): boolean {
  const environment = normalizeBooplyEnvironment(environmentValue, productionFlag ? "production" : "development");

  if (environment === "production" || productionFlag) {
    return false;
  }

  if (typeof configuredValue === "boolean") {
    return configuredValue;
  }

  if (typeof configuredValue === "string") {
    return configuredValue.trim().toLowerCase() !== "false";
  }

  return true;
}

export function mockModerationProviderAllowedForEnvironment(
  environmentValue: string | null | undefined,
  safePlaceholderValue?: string | boolean | null
): boolean {
  const environment = normalizeBooplyEnvironment(environmentValue, "production");

  if (environment !== "production") {
    return true;
  }

  return parseBooleanFlag(safePlaceholderValue) === true;
}

export function validateProductionSafety(options: ProductionSafetyOptions): readonly ProductionSafetyIssue[] {
  const environment = normalizeBooplyEnvironment(options.environment, "local");

  if (environment !== "production") {
    return [];
  }

  const issues: ProductionSafetyIssue[] = [];

  issues.push(...validateProductionUrl("API base URL", options.apiBaseUrl));
  issues.push(...validateProductionUrl("Relay URL", options.relayUrl));

  if (options.fakeBillingEnabled === true) {
    issues.push({
      code: "PRODUCTION_FAKE_BILLING_ENABLED",
      message: "Fake billing routes must be disabled in production."
    });
  }

  if (options.adminMockModeEnabled === true || options.mockDataEnabled === true) {
    issues.push({
      code: "PRODUCTION_MOCK_DATA_ENABLED",
      message: "Production must not use mock data or development-only admin controls by default."
    });
  }

  if (
    options.mockModerationProviderEnabled === true &&
    options.mockModerationSafePlaceholder !== true
  ) {
    issues.push({
      code: "PRODUCTION_MOCK_MODERATION_ENABLED",
      message: "Mock moderation must be disabled in production unless explicitly configured as a safe placeholder."
    });
  }

  return issues;
}

export function validateProductionUrl(label: string, value: string | null | undefined): readonly ProductionSafetyIssue[] {
  if (typeof value !== "string" || value.trim().length === 0) {
    return [{
      code: "PRODUCTION_URL_MISSING",
      message: `${label} must be configured in production.`
    }];
  }

  let parsed: URL;

  try {
    parsed = new URL(value);
  } catch {
    return [{
      code: "PRODUCTION_URL_INVALID",
      message: `${label} must be an absolute URL in production.`
    }];
  }

  if (!["http:", "https:", "ws:", "wss:"].includes(parsed.protocol)) {
    return [{
      code: "PRODUCTION_URL_INVALID_PROTOCOL",
      message: `${label} must use http, https, ws, or wss in production.`
    }];
  }

  if (isLocalhostHostname(parsed.hostname)) {
    return [{
      code: "PRODUCTION_LOCALHOST_URL",
      message: `${label} must not use localhost endpoints in production.`
    }];
  }

  return [];
}

export function isLocalhostHostname(hostname: string): boolean {
  const normalized = hostname.trim().toLowerCase().replace(/^\[|\]$/g, "");

  return normalized === "localhost" ||
    normalized === "127.0.0.1" ||
    normalized === "0.0.0.0" ||
    normalized === "::1";
}

export function parseBooleanFlag(value: string | boolean | null | undefined): boolean | undefined {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value !== "string") {
    return undefined;
  }

  switch (value.trim().toLowerCase()) {
    case "1":
    case "true":
    case "yes":
    case "on":
      return true;
    case "0":
    case "false":
    case "no":
    case "off":
      return false;
    default:
      return undefined;
  }
}
